import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Hero.css";
import { useSearchParams } from "react-router-dom";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import { UploadModal } from "./UploadModal";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import { Icon, useMediaQuery } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const GalleryImageList = () => {
  const [loading, setLoading] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const sliderRef = useRef(null);
  const [searchActive, setSearchActive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [heroImage, setHeroImage] = useState([]);
  const [carouselData, setCarouselData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTermFromURL = searchParams.get("query") || "";

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  });
  const matchDown_xl = useMediaQuery(theme.breakpoints.down("xl"));
  const matchDown_lg = useMediaQuery(theme.breakpoints.down("lg"));
  const matchDown_md = useMediaQuery(theme.breakpoints.down("md"));
  const matchDown_sm = useMediaQuery(theme.breakpoints.down("sm"));
  const fetchHeroImages = async () => {
    try {
      const { data, error } = await supabase
        .from('form_responses')
        .select('*')
        .not('hero_image', 'is', null);

      if (error) throw error;

      const docs = data || [];
      docs.sort((a, b) => a.legacy_id - b.legacy_id);
      setHeroImage(docs);
      setCarouselData(docs);
      console.log("Hero Image", docs);
    } catch (error) {
      console.error("Error fetching documents: ", error);
    } finally {
      setLoading(false);
    }
  };

  const executeSearch = async () => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
    const searchTermLower = searchTerm.toLowerCase();

    if (searchTerm === "") {
      setCarouselData(heroImage);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('form_responses')
        .select('*');

      if (error) throw error;

      const docs = data || [];
      const filteredResults = docs.filter((item) => {
        const title = (item.title || "").toLowerCase();
        const country = (item.country || "").toLowerCase();
        const continent = (item.continent || "").toLowerCase();
        return (
          title.includes(searchTermLower) ||
          country.includes(searchTermLower) ||
          continent.includes(searchTermLower)
        );
      });
      console.log("filtered Results", filteredResults);
      setCarouselData(filteredResults);
      setSearchActive(true);
    } catch (error) {
      console.error("Error searching:", error);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchClick = () => {
    setSearchParams({ query: searchTerm });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      setSearchParams({ query: searchTerm });
    }
  };
  useEffect(() => {
    fetchHeroImages();
  }, []);

  useEffect(() => {
    if (searchTermFromURL) {
      executeSearch(searchTermFromURL);
    } else {
      setCarouselData(heroImage);
    }
  }, [searchTermFromURL]);

  const handleFileUpload = async (imageId, file) => {
    try {
      const filePath = `locations/${imageId}/${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // Update Supabase document with new image URL
      const { error: updateError } = await supabase
        .from('form_responses')
        .update({ img_url: publicUrl })
        .eq('id', imageId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleUploadClick = (imageId) => {
    console.log("ImageId", imageId);
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileUpload(imageId, file);
      }
    };
    fileInput.click();
  };

  const settings = {
    infinite: false,
    speed: 500,
    slidesToShow: 9,
    variableWidth: true,
    swipeToSlide: true,
    dots: false,
    arrows: true,
    centerMode: true,
    centerPadding: "0px",
    focusOnSelect: true,
    lazyLoad: true,
    nextArrow: (
      <ArrowCircleRightIcon
        sx={{
          fontSize: 40,
          color: "white",
          opacity: 0.6,
          "&:hover": {
            color: "white", // Change color on hover (or other style changes)
            opacity: 0.8, // Ensure the icon doesn't disappear
          },
        }}
      />
    ),
    prevArrow: (
      <ArrowCircleLeftIcon
        sx={{
          fontSize: 40,
          color: "white",
          opacity: 0.6,
          "&:hover": {
            color: "white", // Change color on hover (or other style changes)
            opacity: 0.8, // Ensure the icon doesn't disappear
          },
        }}
      />
    ),
    afterChange: (current) => setActiveSlide(current),
  };

  return (
    <div className="hero">
      <div className="data-viz-content">
        <ImageList
          sx={{ margin: 10 }}
          cols={
            matchDown_xl
              ? matchDown_lg
                ? matchDown_md
                  ? matchDown_sm
                    ? 2
                    : 3
                  : 4
                : 6
              : 7
          }
        >
          {carouselData.map((item) => (
            <ImageListItem key={item.id}>
              <img
                src={
                  item.hero_image ? item.hero_image : (Array.isArray(item.image_url) ? item.image_url[0] : item.image_url)
                }
                alt={item.location}
              />
              <ImageListItemBar
                title={item.title}
                subtitle={item.location}
                actionIcon={
                  <IconButton
                    sx={{ color: "rgba(255, 255, 255, 0.54)" }}
                    aria-label={`info about ${item.location}`}
                  >
                    <InfoIcon />
                  </IconButton>
                }
                sx={{
                  "& .MuiImageListItemBar-title": { color: "white" }, //styles for title
                  "& .MuiImageListItemBar-subtitle": { color: "white" }, //styles for subtitle
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </div>
    </div>
  );
};

export default GalleryImageList;
